import React from 'react';
import { Download, Loader2, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DinacharyaLotusIcon from './DinacharyaLotusIcon';

type DinacharyaActionsBarProps = {
  generatedForDate?: string | null;
  onPrint: () => void;
  onDownloadPdf: () => void;
  isPdfGenerating?: boolean;
};

const formatDateLabel = (isoDate: string, locale?: string) => {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString(locale || undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const DinacharyaActionsBar: React.FC<DinacharyaActionsBarProps> = ({
  generatedForDate,
  onPrint,
  onDownloadPdf,
  isPdfGenerating = false,
}) => {
  const { t, i18n } = useTranslation('chat');
  const reportLabel = t('chat.dinacharya.actions.routine_report', 'Daily Vedic Routine');
  const generatedDateLabel = generatedForDate ? formatDateLabel(generatedForDate, i18n.language) : '';

  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-950">
      <div className="flex items-center gap-2 font-semibold">
        <DinacharyaLotusIcon className="h-5 w-5 shrink-0 text-amber-700" />
        {reportLabel}{generatedDateLabel ? ` - ${generatedDateLabel}` : ''}
      </div>
      <div className="dinacharya-no-print flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onPrint}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-white/90 px-2.5 py-1.5 font-medium text-amber-950 shadow-sm transition hover:border-amber-300 hover:bg-white"
        >
          <Printer className="h-3.5 w-3.5" />
          {t('chat.dinacharya.actions.print', 'Print')}
        </button>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={isPdfGenerating}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-white/90 px-2.5 py-1.5 font-medium text-amber-950 shadow-sm transition hover:border-amber-300 hover:bg-white disabled:cursor-wait disabled:opacity-70"
        >
          {isPdfGenerating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {isPdfGenerating
            ? t('chat.dinacharya.actions.preparing_pdf', 'Preparing PDF')
            : t('chat.dinacharya.actions.download_pdf', 'Download PDF')}
        </button>
      </div>
    </div>
  );
};

export default DinacharyaActionsBar;
